import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { useIntl } from 'react-intl';

import useAutoformTableMetadata from './hooks/useAutoformTableMetadata';
import useAutoformFormInitialValues from './hooks/useAutoformFormInitialValues';
import useAutoformMetadata from './hooks/useAutoformMetadata';
import { saveAutoformRecords } from '../../store/autoform/autoformApi';
import {
    getDtoFromDataValueObject,
    getPrimaryKeyColName,
    getTableIdFromInputName,
    getTableFormKey,
    getItemFormKey,
} from './utils/autoformUtils';
import useCommonToast from '../../hooks/useCommonToast';
import AutoformUiContext from './utils/AutoformUiContext';

const ItemFormContainer = ({ children, includeLevel2Values, onNodeRename }) => {
    const { hierarchicalTables, tableMap, metadata } = useAutoformMetadata();

    const dispatch = useDispatch();
    const toast = useCommonToast();
    const intl = useIntl();
    const level1TableId = hierarchicalTables.level1[0].id;
    const { selectedItem: selectedLevel1Item, descriptionColumnName } = useAutoformTableMetadata(level1TableId);
    const initialValues = useAutoformFormInitialValues({ includeLevel2Values, selectedLevel1Item });
    const { setEditingState } = useContext(AutoformUiContext);

    // TODO: Fetch single Level 1 record based on selectedLevel1Item with loader so as to not flash data

    const onFormSubmit = async (values, actions) => {
        const formValues = Object.assign({}, values);

        actions.setSubmitting(true);

        const getRecords = Object.keys(formValues).map((data) => {
            const tableId = getTableIdFromInputName(data);
            const primaryKeyRecordName = getPrimaryKeyColName(tableMap[tableId].columns);
            const tableMetadata = metadata.tables.find((t) => t.id === tableId);

            return {
                records: Object.keys(formValues[data]).map((key) =>
                    getDtoFromDataValueObject(formValues[data][key], tableMetadata)
                ),
                tableId,
                primaryKeyRecordName,
            };
        });
        const parameters = metadata.parameters.map((parameter) => parameter.value);

        try {
            await Promise.all(
                getRecords.map(({ tableId, records, primaryKeyRecordName }) => {
                    if (tableMap[tableId]?.canUpdate && records.length) {
                        return dispatch(
                            saveAutoformRecords({
                                tableId,
                                records,
                                primaryKeyRecordName,
                                parameters,
                            })
                        ).unwrap();
                    }

                    return { tableId, records, primaryKeyRecordName };
                })
            );
            const itemDescription =
                values[getTableFormKey(level1TableId)] &&
                values[getTableFormKey(level1TableId)][getItemFormKey(selectedLevel1Item)] &&
                values[getTableFormKey(level1TableId)][getItemFormKey(selectedLevel1Item)][descriptionColumnName];
            onNodeRename && onNodeRename(itemDescription);
            toast(intl.formatMessage({ id: 'renewable_save_changes_success' }));
            setEditingState({});
        } catch (error) {
            if (error.response && error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
        }

        actions.setSubmitting(false);
    };

    return (
        <>
            <Formik validateOnChange={false} enableReinitialize initialValues={initialValues} onSubmit={onFormSubmit}>
                {({ isSubmitting, dirty, isValid, values }) => <Form>{children}</Form>}
            </Formik>
        </>
    );
};

export default ItemFormContainer;
